/**
 * TripMenuSheet - Mobile "⋯" bottom sheet: entry point to 成員管理/投票/邀請連結/分享旅程
 * Design: tripways.pen node z2tbg2
 *
 * Desktop users reach the same destinations via DesktopSidebar's「旅程管理」section.
 */

import { useNavigate } from 'react-router-dom';
import { Pencil, Users, Vote, Link as LinkIcon, Share, ChevronRight } from 'lucide-react';
import Modal from '../common/Modal';
import InviteLinkSheet from '../members/InviteLinkSheet';
import { useState } from 'react';

interface TripMenuSheetProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: string;
  memberCount?: number;
  openPollCount?: number;
  onShareTrip?: () => void;
}

export default function TripMenuSheet({
  isOpen,
  onClose,
  tripId,
  memberCount,
  openPollCount,
  onShareTrip,
}: TripMenuSheetProps) {
  const navigate = useNavigate();
  const [showInviteSheet, setShowInviteSheet] = useState(false);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="旅程選單" variant="bottom-sheet">
        <div className="flex flex-col">
          <MenuItem
            icon={Pencil}
            label="編輯旅程"
            description="修改旅程名稱、日期等資訊"
            onClick={() => go(`/trips/${tripId}/edit`)}
          />
          <MenuItem
            icon={Users}
            label="成員管理"
            description="查看與管理旅程成員"
            badge={memberCount}
            onClick={() => go(`/trips/${tripId}/members`)}
          />
          <MenuItem
            icon={Vote}
            label="投票"
            description="發起或參與旅程投票"
            badge={openPollCount}
            onClick={() => go(`/trips/${tripId}/polls`)}
          />
          <MenuItem
            icon={LinkIcon}
            label="邀請連結"
            description="產生邀請連結分享給朋友"
            onClick={() => {
              onClose();
              setShowInviteSheet(true);
            }}
          />
          <MenuItem
            icon={Share}
            label="分享旅程"
            description="透過其他方式分享"
            onClick={() => {
              onClose();
              onShareTrip?.();
            }}
          />
        </div>
        <div className="h-px bg-slate-100" />
        <button
          onClick={onClose}
          className="w-full text-center px-5 py-3.5 min-h-[44px] text-[15px] font-medium text-neutral-400"
        >
          取消
        </button>
      </Modal>

      <InviteLinkSheet isOpen={showInviteSheet} onClose={() => setShowInviteSheet(false)} tripId={tripId} />
    </>
  );
}

function MenuItem({
  icon: Icon,
  label,
  description,
  badge,
  onClick,
}: {
  icon: typeof Users;
  label: string;
  description: string;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button onClick={onClick} className="flex items-center gap-3.5 px-5 py-3.5 text-left w-full min-h-[44px]">
      <div className="w-10 h-10 rounded-[10px] bg-[#f5f8f8] flex items-center justify-center shrink-0">
        <Icon size={20} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-medium text-slate-900">{label}</p>
        <p className="text-xs text-slate-400">{description}</p>
      </div>
      {badge !== undefined ? (
        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
          <span className="text-[11px] font-semibold text-white">{badge}</span>
        </div>
      ) : (
        <ChevronRight size={18} className="text-neutral-300 shrink-0" />
      )}
    </button>
  );
}
